import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, User, Building2, ArrowRight, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { validateSignUpForm, getPasswordStrength, getPasswordStrengthLabel, getPasswordStrengthColor } from '../utils/validation';
import '../styles/SignUp.css';

const BUSINESS_TYPES = [
  { value: 'ltd', label: 'Limited Company' },
  { value: 'sole_trader', label: 'Sole Trader' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'charity', label: 'Charity' },
  { value: 'agency', label: 'Agency' },
  { value: 'other', label: 'Other' },
];

const SignUp = () => {
  const navigate = useNavigate();
  const { signup, isLoading, error: authError, clearError } = useAuth();
  const { error: showError } = useToast();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    businessType: 'ltd',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [errors, setErrors] = useState({});
  const [localError, setLocalError] = useState('');

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === 'acceptTerms') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (name === 'password') {
      setPasswordStrength(getPasswordStrength(value));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (localError) {
      setLocalError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    const validation = validateSignUpForm(
      formData.firstName,
      formData.email,
      formData.password,
      formData.confirmPassword,
      formData.acceptTerms
    );

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    if (!formData.companyName || formData.companyName.trim().length < 2) {
      setErrors(prev => ({ ...prev, companyName: 'Company name must be at least 2 characters' }));
      return;
    }

    try {
      const nameParts = formData.firstName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      // Step 1 of 2: creates the account but does NOT log in yet — email must be
      // verified via OTP first. VerifyOtp.jsx handles step 2 and auto signs in on success.
      const response = await signup({
        firstName,
        lastName,
        email: formData.email,
        password: formData.password,
        companyName: formData.companyName.trim(),
        businessType: formData.businessType,
      });

      if (response && response.success) {
        navigate('/verify-otp', {
          state: {
            purpose: 'signup',
            email: response.data.email,
            challengeToken: response.data.challengeToken,
            devCode: response.data.devCode,
          },
        });
      }
    } catch (err) {
      const errorMsg = authError || err?.message || 'Sign up failed. Please try again.';
      setLocalError(errorMsg);
      showError(errorMsg);
      console.error('Sign up error:', err);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <motion.div
      className="signup-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="signup-container">
        <motion.div
          className="signup-brand"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="brand-content">
            <Link to="/" className="brand-logo">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="Effective Global Staff"
                className="brand-logo-img"
              />
            </Link>
            <h1>Join Us Today</h1>
            <p>Start managing your payroll securely and efficiently</p>

            <div className="brand-benefits">
              {[
                'Free 14-day trial',
                'No credit card required',
                'Full feature access',
                'Expert support'
              ].map((benefit, idx) => (
                <div key={idx} className="benefit-item">
                  <CheckCircle size={20} />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="brand-bg">
              <div className="blob blob-1"></div>
              <div className="blob blob-2"></div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="signup-form-section"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.form
            className="signup-form"
            onSubmit={handleSubmit}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Back to Home */}
            <motion.div variants={itemVariants} style={{ marginBottom: '1rem' }}>
              <Link to="/" className="back-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: '#6b7280', textDecoration: 'none' }}>
                <ArrowLeft size={16} />
                Back to Home
              </Link>
            </motion.div>

            <motion.h2 variants={itemVariants}>Create Account</motion.h2>
            <motion.p variants={itemVariants} className="form-subtitle">
              Join hundreds of UK businesses managing payroll with Effective Global Staff
            </motion.p>

            {(localError || authError) && (
              <motion.div
                className="error-alert"
                variants={itemVariants}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <AlertCircle size={18} />
                <span>{localError || authError}</span>
              </motion.div>
            )}

            <motion.div className="form-group" variants={itemVariants}>
              <label htmlFor="firstName">Full Name</label>
              <div className={`input-wrapper ${errors.firstName ? 'error' : ''}`}>
                <User size={18} className="input-icon" />
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John Doe"
                  disabled={isLoading}
                  required
                />
              </div>
              {errors.firstName && (
                <motion.p className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {errors.firstName}
                </motion.p>
              )}
            </motion.div>

            {/* Company Name — required by the backend to create the organisation */}
            <motion.div className="form-group" variants={itemVariants}>
              <label htmlFor="companyName">Company Name</label>
              <div className={`input-wrapper ${errors.companyName ? 'error' : ''}`}>
                <Building2 size={18} className="input-icon" />
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Acme Ltd"
                  disabled={isLoading}
                  required
                />
              </div>
              {errors.companyName && (
                <motion.p className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {errors.companyName}
                </motion.p>
              )}
            </motion.div>

            {/* Business Type — backend requires this too (free text, no enum enforced) */}
            <motion.div className="form-group" variants={itemVariants}>
              <label htmlFor="businessType">Business Type</label>
              <div className="input-wrapper">
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  disabled={isLoading}
                >
                  {BUSINESS_TYPES.map((bt) => (
                    <option key={bt.value} value={bt.value}>{bt.label}</option>
                  ))}
                </select>
              </div>
            </motion.div>

            <motion.div className="form-group" variants={itemVariants}>
              <label htmlFor="email">Email Address</label>
              <div className={`input-wrapper ${errors.email ? 'error' : ''}`}>
                <Mail size={18} className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  disabled={isLoading}
                  required
                />
              </div>
              {errors.email && (
                <motion.p className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {errors.email}
                </motion.p>
              )}
            </motion.div>

            <motion.div className="form-group" variants={itemVariants}>
              <label htmlFor="password">Password</label>
              <div className={`input-wrapper ${errors.password ? 'error' : ''}`}>
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {formData.password && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div
                      className="strength-fill"
                      style={{
                        width: `${(passwordStrength / 4) * 100}%`,
                        backgroundColor: getPasswordStrengthColor(passwordStrength)
                      }}
                    />
                  </div>
                  <span className="strength-label">{getPasswordStrengthLabel(passwordStrength)}</span>
                </div>
              )}
              {errors.password && (
                <motion.p className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {errors.password}
                </motion.p>
              )}
            </motion.div>

            <motion.div className="form-group" variants={itemVariants}>
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className={`input-wrapper ${errors.confirmPassword ? 'error' : ''}`}>
                <Lock size={18} className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <motion.p className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {errors.confirmPassword}
                </motion.p>
              )}
            </motion.div>

            <motion.div className="form-options" variants={itemVariants}>
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms" className="link">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="link">
                    Privacy Policy
                  </Link>
                </span>
              </label>
              {errors.acceptTerms && (
                <motion.p className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {errors.acceptTerms}
                </motion.p>
              )}
            </motion.div>

            <motion.button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={isLoading}
              variants={itemVariants}
              whileHover={!isLoading ? { scale: 1.02 } : {}}
              whileTap={!isLoading ? { scale: 0.98 } : {}}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
              {!isLoading && <ArrowRight size={18} />}
            </motion.button>

            <motion.p className="signin-prompt" variants={itemVariants}>
              Already have an account?{' '}
              <Link to="/signin" className="signin-link">
                Sign in here
              </Link>
            </motion.p>
          </motion.form>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SignUp;